import { BriefApplicationResponseDto, NewBriefShoutDto } from '../../../../common/general-types/briefs.dto';
import { getSocialChannelLabel } from '../../../insights/tabs/creator/molecules/application-drawer/molecules/application-actions/application-actions.helpers';
import {
  briefCrossChannels,
  crossPlatformState,
  togglableChannels,
} from '../../../manage-brief-tabs/tabs/assignment/cross-platform-state.util';

/**
 * Copy and grouping rules for the Review application modal.
 *
 * Pure functions over the application and the brief, so the wording can be tested
 * without standing up a component — the header sentence and the "not put forward"
 * list are the fiddliest part of the design and the easiest to get subtly wrong.
 */

/** `1 network` / `4 networks`. */
export function networkCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'network' : 'networks'}`;
}

/**
 * Why a channel the brief offers isn't on the table for this creator.
 *
 * `no-account` and `not-offered` must stay distinct: the first is a gap the creator
 * can close by linking an account, the second is a choice they already made. Telling
 * a brand "no connected account" about a creator who simply declined is wrong.
 */
export type NotPutForwardReason = 'no-account' | 'not-offered';

export interface NotPutForwardEntry {
  socialChannel: number;
  label: string;
  reason: NotPutForwardReason;
}

/** Channels the brief offers that this creator did not put forward, with the reason. */
export function notPutForwardEntries(
  application: BriefApplicationResponseDto | undefined,
  shoutBrief: NewBriefShoutDto | undefined | null,
): NotPutForwardEntry[] {
  if (!application) {
    return [];
  }

  const offered = new Set(togglableChannels(application, shoutBrief));

  return briefCrossChannels(shoutBrief)
    .filter((socialChannel) => !offered.has(socialChannel))
    .map((socialChannel) => ({
      socialChannel,
      label: getSocialChannelLabel(socialChannel),
      reason:
        crossPlatformState(application, socialChannel) === 'none'
          ? ('no-account' as const)
          : ('not-offered' as const),
    }));
}

/**
 * The list for the info panel, e.g.
 * `YouTube Shorts (no connected account), Snapchat (not offered)`.
 */
export function notPutForwardSentence(entries: readonly NotPutForwardEntry[]): string {
  return entries
    .map((entry) =>
      `${entry.label} (${entry.reason === 'no-account' ? 'no connected account' : 'not offered'})`,
    )
    .join(', ');
}

/**
 * Shape of the sub-line under the total.
 *
 * Money formatting stays in the template so it goes through `CurrencyPipe` and the
 * brief's own currency — this only decides which sentence is true.
 */
export type TotalSubLine =
  | { kind: 'primary-only' }
  | { kind: 'split'; includedCount: number };

export function totalSubLine(includedCount: number): TotalSubLine {
  return includedCount === 0 ? { kind: 'primary-only' } : { kind: 'split', includedCount };
}

export interface HeaderDescription {
  /** True when the creator offered at least one extra network. */
  hasPutForward: boolean;
  creatorName: string;
  primaryLabel: string;
  putForwardCount: number;
  /** Tier the creator's rate came from, e.g. `Established (100K+)`; null when unknown. */
  tier: string | null;
}

export function headerDescription(input: {
  creatorFirstName?: string | null;
  primarySocialChannel: number | undefined | null;
  putForwardCount: number;
  tierTitle?: string | null;
  tierFollowersFrom?: number | null;
  tierFollowersTo?: number | null;
}): HeaderDescription {
  return {
    hasPutForward: input.putForwardCount > 0,
    creatorName: input.creatorFirstName?.trim() || 'This creator',
    primaryLabel:
      input.primarySocialChannel == null
        ? 'the primary network'
        : getSocialChannelLabel(input.primarySocialChannel),
    putForwardCount: input.putForwardCount,
    tier: input.tierTitle?.trim() ? input.tierTitle.trim() : null,
  };
}

/**
 * What the line under a network's name should say about the audience.
 *
 * A state rather than a string, so the caller formats the number with the app's own
 * formatter and the `null` case can never reach it. `FormatService.convertIntToString`
 * renders any falsy input as `"0"`, so an unknown count passed straight through would
 * claim the creator has no followers — which is the misreading this whole change exists
 * to stop.
 */
export type FollowerMetric =
  | { kind: 'known'; followers: number }
  | { kind: 'loading' }
  | { kind: 'unavailable' };

export function followerMetric(followers: number | null, loading: boolean): FollowerMetric {
  if (followers !== null) {
    return { kind: 'known', followers };
  }
  return loading ? { kind: 'loading' } : { kind: 'unavailable' };
}

/**
 * Whether a network row should admit that its rate is missing rather than nil.
 *
 * Only cross-posts say "no audience bonus at this tier": the primary row's figure is the
 * whole fee, so a zero there means something else entirely.
 */
export function showsNoBonusNote(input: {
  required: boolean;
  rate: number | null;
}): boolean {
  return !input.required && input.rate !== null && input.rate <= 0;
}
